import { Link } from "react-router-dom";
import Facebook from "../../images/social_icons/facebook-f.svg";
import Instagram from "../../images/social_icons/instagram.svg";
import Twitter from "../../images/social_icons/twitter.svg";
import Linkedin from "../../images/social_icons/linkedin.svg";
import Youtube from "../../images/social_icons/youtube.svg";
import useThemeStore from "../../store/themeStore";
import { ThemeProps } from "../../utils/theme";
import GenInTouch from "../GenInTouch";
import { useState, useEffect } from "react";
// Add a type extending ThemeProps to include siteName
interface ExtendedThemeProps extends ThemeProps {
  siteName: string;  // Changed from optional to required to match ThemeProps
}

const Footer = () => {
  const getThemes = useThemeStore(
    (state) => state.theme as unknown as ExtendedThemeProps
  );
  
  // State to safely handle the domain name
  const [domainName, setDomainName] = useState("");
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  
  // Process the domain name safely once theme data is available
  useEffect(() => {
    if (getThemes) {
      try {
        // Try siteName first, then name as fallback
        const nameToUse = getThemes.siteName || getThemes.name || "";
        setDomainName(cleanDomainName(nameToUse));
      } catch (error) {
        console.error("Error processing domain name:", error);
        setDomainName("");
      }
    }
  }, [getThemes]);

  const isWhiteOrShade = (color: string) => {
    if (!color) return false;
    if (color.toLowerCase() === "white" || color === "rgba(255,255,255,1)") return true;
    if (/^#fff[a-f0-9]{0,3}$/i.test(color)) return true;
    const rgba = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
    if (rgba) {
      const [, r, g, b] = rgba.map(Number);
      return r >= 240 && g >= 240 && b >= 240;
    }
    return false;
  };

  const cleanDomainName = (name: string | undefined | null): string => {
    // Handle undefined, null, or non-string values
    if (!name || typeof name !== 'string') {
      return '';
    }

    try {
      // Remove common prefixes
      const prefixes = ['properties-', 'off-market-', 'listings-'];
      let cleanedName = name;
      
      for (const prefix of prefixes) {
        if (cleanedName.startsWith(prefix)) {
          cleanedName = cleanedName.substring(prefix.length);
          break;
        }
      }

      // Handle subdomains
      if (cleanedName.includes('.')) {
        const parts = cleanedName.split('.');
        // If it's a subdomain (like properties.trustbricks.co.uk)
        if (parts.length > 2) {
          // Remove the first part (subdomain) and join the rest
          cleanedName = parts.slice(1).join('.');
        }
      }

      return cleanedName;
    } catch (error) {
      console.error("Error cleaning domain name:", error);
      return name || '';
    }
  };
  
  return (
    <>
      {getThemes && getThemes.website === "https://trustbricks.co.uk" && (
        <footer
          className="w-full py-10 px-4 sm:px-6 lg:px-8 mx-auto"
          style={{ background: getThemes?.color1 || '#000' }}
        >
          {/* <!-- Grid --> */}
          <div className="max-w-7xl m-auto grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6 mb-10">
            <div className="col-span-full hidden lg:col-span-1 lg:block text-left">
              <Link
                className="flex-none text-xl font-semibold dark:text-white"
                to="/"
                aria-label="Trustbricks"
              >
                <img
                  src={getThemes?.logo || ''}
                  className="w-48"
                  alt="Trustbricks logo"
                />
              </Link>
              <p className="mt-3 text-xs sm:text-sm text-white-f2f9fa">
                Unit 6, Church Farm, Church Road, Suffolk<br></br>
                United Kingdom, IP29 5AX
              </p>
            </div>
            {/* <!-- End Col --> */}

            <div className="text-left text-white-f2f9fa">
              <h4 className="text-lg font-medium text-white-f2f9fa uppercase">
                Stay Connected
              </h4>

              <div className="my-5 gap-5 flex text-sm">
                <Link
                  className="flex items-center sm:py-2"
                  to="https://www.facebook.com/trustbricks"
                >
                  <img src={Facebook} className="w-6 h-6" alt="Facebook icon" />
                </Link>
                <Link
                  className="flex items-center sm:py-2"
                  to="https://www.instagram.com/trust_bricks/"
                >
                  <img src={Instagram} className="w-6 h-6" alt="Instagram icon" />
                </Link>
                <Link
                  className="flex items-center sm:py-2"
                  to="https://twitter.com/trustbricks/"
                >
                  <img src={Twitter} className="w-6 h-6" alt="Twitter icon" />
                </Link>
                <Link
                  className="flex items-center sm:py-2"
                  to="https://www.linkedin.com/company/trustbricks/"
                >
                  <img src={Linkedin} className="w-6 h-6" alt="LinkedIn icon" />
                </Link>
                <Link
                  className="flex items-center sm:py-2"
                  to="https://www.youtube.com/channel/UCtbHPeoFa4tQaWSJLrOsrcQ/"
                >
                  <img src={Youtube} className="w-6 h-6" alt="YouTube icon" />
                </Link>
              </div>
              <img
                src="https://trustbricks.co.uk/wp-content/uploads/2022/08/PRS_Logo_v2.png"
                alt="PRS Logo"
                className="w-2/3 py-2"
              />
            </div>
            {/* <!-- End Col --> */}
          </div>
          {/* <!-- End Grid --> */}

          <div className="max-w-7xl m-auto pt-5 mt-5">
            <div className="flex flex-row justify-evenly w-full">
              <div className="space-x-4 text-sm lg:w-1/2 sm:w-full flex">
                <p className="text-white-f2f9fa text-left w-full">
                  Copyright © {new Date().getFullYear()} {domainName}
                </p>
              </div>
              <div className="space-x-4 text-sm ml-4 lg:w-1/2 sm:w-full flex">
                <p className="text-white-f2f9fa w-full text-right">
                  <Link
                    className=""
                    to="https://trustbricks.co.uk/privacy-policy/"
                  >
                    Privacy policy
                  </Link>
                  <span className="px-1">|</span>
                  <Link
                    className=""
                    to="https://trustbricks.co.uk/legal-notice/"
                  >
                    Legal notice
                  </Link>
                  <span className="px-1">|</span>
                  <button
                    onClick={() => setIsTermsModalOpen(true)}
                    className="text-white-f2f9fa hover:text-gray-300 transition-colors duration-200"
                  >
                    Terms & Conditions
                  </button>
                </p>
              </div>

              {/* <!-- End Col --> */}
            </div>
          </div>
        </footer>
       )}
       {getThemes && getThemes.website !== "https://trustbricks.co.uk" && (
  <footer
    className="w-full py-5 px-4 sm:px-6 lg:px-8 mx-auto justify-center flex"
    style={{ background: getThemes?.color1 || '#000' }}
  >
    <div>
      <p
        className="text-left w-full"
        style={{ color: getThemes?.color1 && isWhiteOrShade(getThemes.color1) ? "black" : (getThemes?.txtColor || '#fff') }}
      >
        Copyright © {new Date().getFullYear()} {domainName}
        <span className="px-1">|</span>
        <button
          onClick={() => setIsTermsModalOpen(true)}
          className="hover:text-gray-300 transition-colors duration-200"
          style={{ color: getThemes?.color1 && isWhiteOrShade(getThemes.color1) ? "black" : (getThemes?.txtColor || '#fff') }}
        >
          Terms & Conditions
        </button>
      </p>
    </div>
  </footer>
)}

{/* Terms & Conditions Modal */}
{isTermsModalOpen && (
  <div className="fixed inset-0 z-[999999] overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center">
    <div className="bg-white rounded-lg shadow-xl w-11/12 h-5/6 max-w-4xl flex flex-col" style={{ backgroundColor: 'white' }}>
      <div className="flex justify-between items-center p-4 border-b">
        <h3 className="text-xl font-bold">Terms & Conditions</h3>
        <button 
          onClick={() => setIsTermsModalOpen(false)}
          className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="flex-1 p-4">
        <iframe 
          src="https://mypropertygroup.net/terms-conditions-2025"
          className="w-full h-full border-0"
          title="Terms & Conditions"
        />
      </div>
    </div>
  </div>
)}

<GenInTouch />
</>
);

 };
 
 export default Footer;